import React, { useCallback } from 'react';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

const errorMessage = (error) => error?.response?.data?.message || $t('Something went wrong');

function ApproverRules({ rules }) {
    return (
        <div className="tw-space-y-2">
            <div>{$t('This user is an approver on these approval rules. They stay on the rules while deactivated, so each rule should be updated:')}</div>
            <ul className="tw-mb-0 tw-list-disc tw-ps-5 tw-text-sm">
                {rules.map((rule) => (
                    <li key={`${rule.kind}-${rule.approval_settings_id}`}>
                        {/* The trigger arrives already translated by the server. */}
                        {rule.event_name} › {rule.trigger || $t('Approval')}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * The member actions shared by the table and the panel. Each one confirms
 * first, then reports the refreshed member to `onDone`.
 */
export default function useOrganisationUserActions({ usersUrl, onDone }) {
    const confirm = useConfirmationModal();
    const toast = useToast();

    const post = useCallback(
        (user, action, successMessage) =>
            axios
                .post(`${usersUrl}/${user.id}/${action}`)
                .then(({ data }) => {
                    toast.success(successMessage);
                    onDone(data.data);
                })
                .catch((error) => toast.error(errorMessage(error))),
        [usersUrl, onDone, toast],
    );

    const deactivate = useCallback(
        (user) =>
            axios
                .get(`${usersUrl}/${user.id}/approver-rules`)
                .then(({ data }) =>
                    confirm({
                        title: $t('Deactivate {{name}}', { name: user.name }),
                        message:
                            data.data.length > 0 ? (
                                <ApproverRules rules={data.data} />
                            ) : (
                                $t('{{name}} will lose access to this organisation and its projects from their next request.', { name: user.name })
                            ),
                        warning: $t('Their memberships and roles are kept, so reactivating them restores access.'),
                        onConfirm: () => post(user, 'deactivate', $t('The user was deactivated')),
                    }),
                )
                .catch((error) => toast.error(errorMessage(error))),
        [usersUrl, confirm, post, toast],
    );

    const reactivate = useCallback(
        (user) =>
            confirm({
                title: $t('Reactivate {{name}}', { name: user.name }),
                message: $t('{{name}} will regain access to this organisation and its projects.', { name: user.name }),
                onConfirm: () => post(user, 'reactivate', $t('The user was reactivated')),
            }),
        [confirm, post],
    );

    const resendInvite = useCallback(
        (user) =>
            confirm({
                title: $t('Resend invite'),
                message: $t('Send the invitation to {{email}} again?', { email: user.email }),
                onConfirm: () => post(user, 'invite', $t('The invitation was sent')),
            }),
        [confirm, post],
    );

    const resetMfa = useCallback(
        (user) =>
            confirm({
                title: $t('Reset MFA for {{name}}', { name: user.name }),
                message: $t('Their MFA set-up is removed and they set it up again at their next login.'),
                warning: $t(
                    'This affects every organisation they belong to and signs them out everywhere. Access tokens issued to integrations are not revoked.',
                ),
                onConfirm: () => post(user, 'mfa-reset', $t('MFA was reset')),
            }),
        [confirm, post],
    );

    const addToProject = useCallback(
        (user, eventId, role) =>
            axios
                .post(`${usersUrl}/${user.id}/projects`, { event_id: eventId, role })
                .then(({ data }) => {
                    toast.success($t('{{name}} was added to the project', { name: user.name }));
                    onDone(data.data);

                    return true;
                })
                .catch((error) => {
                    toast.error(errorMessage(error));

                    return false;
                }),
        [usersUrl, onDone, toast],
    );

    /**
     * What removing the member from the project would run into: the approval
     * rules that block it and whether they are its only Event Admin.
     */
    const removalCheck = useCallback(
        (user, project) =>
            axios
                .get(`${usersUrl}/${user.id}/projects/${project.event_id}/removal`)
                .then(({ data }) => data.data)
                .catch((error) => {
                    toast.error(errorMessage(error));

                    return null;
                }),
        [usersUrl, toast],
    );

    /**
     * Resolves to the server's fresh preflight when the removal was refused
     * because of approval rules, so the dialog can show them; otherwise null.
     */
    const removeFromProject = useCallback(
        (user, project, confirmLastAdmin) =>
            axios
                .delete(`${usersUrl}/${user.id}/projects/${project.event_id}`, { data: { confirm_last_admin: !!confirmLastAdmin } })
                .then(({ data }) => {
                    toast.success($t('{{name}} was removed from {{project}}', { name: user.name, project: project.name }));
                    onDone(data.data);

                    return null;
                })
                .catch((error) => {
                    const response = error?.response?.data;

                    if (response?.error_code === 'APPROVER_RULES') {
                        return { rules: response.rules, link_url: response.link_url, is_last_event_admin: false };
                    }

                    if (response?.error_code === 'LAST_EVENT_ADMIN') {
                        return { rules: [], link_url: null, is_last_event_admin: true };
                    }

                    toast.error(errorMessage(error));

                    return null;
                }),
        [usersUrl, onDone, toast],
    );

    return { deactivate, reactivate, resendInvite, resetMfa, addToProject, removalCheck, removeFromProject };
}
